import React, { useState } from 'react';
import { Car, ChevronRight, Check } from 'lucide-react';
import { getPlateFormat } from '../lib/plateFormats';
import '../styles/CarSetupForm.css';

const CarSetupForm = ({ onComplete, onCancel, initialData = null, language = 'en' }) => {
    const [step, setStep] = useState(1);
    const region = localStorage.getItem('region') || 'US';
    const plateFormat = getPlateFormat(region);
    const [carData, setCarData] = useState({
        name: initialData?.name || '',
        brand: initialData?.brand || '',
        model: initialData?.model || '',
        engine: initialData?.engine || '',
        sedanType: initialData?.sedanType || '',
        year: initialData?.year || '',
        mileage: initialData?.mileage || '',
        plateNumber: initialData?.plateNumber || ''
    });

    const translations = {
        en: {
            title: 'Setup Your Car',
            step: 'Step',
            of: 'of',
            next: 'Next',
            finish: 'Finish',
            cancel: 'Cancel',
            carName: 'Car Name',
            carNamePlaceholder: 'e.g., My Daily Driver',
            brand: 'Brand',
            brandPlaceholder: 'e.g., Toyota, BMW, Ford',
            model: 'Model',
            modelPlaceholder: 'e.g., Camry, 3 Series, Mustang',
            engine: 'Engine',
            enginePlaceholder: 'e.g., 2.5L V6, 2.0L Turbo',
            sedanType: 'Body Type',
            sedanTypePlaceholder: 'e.g., Sedan, SUV, Coupe',
            year: 'Year of Production',
            yearPlaceholder: 'e.g., 2020',
            mileage: 'Current Mileage',
            mileagePlaceholder: 'e.g., 45000',
            plateNumber: 'License Plate',
            plateNumberPlaceholder: plateFormat.placeholder
        },
        bg: {
            title: 'Настройка на автомобила',
            step: 'Стъпка',
            of: 'от',
            next: 'Напред',
            finish: 'Завърши',
            cancel: 'Отказ',
            carName: 'Име на автомобила',
            carNamePlaceholder: 'напр., Моята кола',
            brand: 'Марка',
            brandPlaceholder: 'напр., Toyota, BMW, Ford',
            model: 'Модел',
            modelPlaceholder: 'напр., Camry, 3 Series, Mustang',
            engine: 'Двигател',
            enginePlaceholder: 'напр., 2.5L V6, 2.0L Turbo',
            sedanType: 'Тип каросерия',
            sedanTypePlaceholder: 'напр., Седан, SUV, Купе',
            year: 'Година на производство',
            yearPlaceholder: 'напр., 2020',
            mileage: 'Текущ пробег',
            mileagePlaceholder: 'напр., 45000',
            plateNumber: 'Регистрационен номер',
            plateNumberPlaceholder: plateFormat.placeholder
        }
    };

    const t = translations[language] || translations.en;

    const steps = [
        { field: 'name', label: t.carName, placeholder: t.carNamePlaceholder, type: 'text', optional: true },
        { field: 'brand', label: t.brand, placeholder: t.brandPlaceholder, type: 'text' },
        { field: 'model', label: t.model, placeholder: t.modelPlaceholder, type: 'text' },
        { field: 'engine', label: t.engine, placeholder: t.enginePlaceholder, type: 'text' },
        { field: 'sedanType', label: t.sedanType, placeholder: t.sedanTypePlaceholder, type: 'text' },
        { field: 'year', label: t.year, placeholder: t.yearPlaceholder, type: 'number' },
        { field: 'mileage', label: t.mileage, placeholder: t.mileagePlaceholder, type: 'number' },
        { field: 'plateNumber', label: t.plateNumber, placeholder: t.plateNumberPlaceholder, type: 'text' }
    ];

    const currentStep = steps[step - 1];

    const handleNext = () => {
        const value = carData[currentStep.field];
        const canProceed = currentStep.optional || (value && String(value).trim());
        if (canProceed) {
            if (step < steps.length) {
                setStep(step + 1);
            } else {
                handleComplete();
            }
        }
    };

    const handleComplete = () => {
        const colors = ['#3b82f6', '#ef4444', '#fbbf24', '#22c55e', '#a855f7', '#ec4899'];
        const randomColor = colors[Math.floor(Math.random() * colors.length)];

        onComplete({
            ...carData,
            name: carData.name.trim() || 'My Car',
            color: initialData?.color || randomColor,
            id: initialData?.id || Date.now().toString(),
            created_at: initialData?.created_at || new Date().toISOString()
        });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleNext();
        }
    };

    // Single Page Edit Mode
    if (initialData) {
        return (
            <div className="car-setup-container">
                <div className="car-setup-background">
                    <div className="setup-blob blob-1"></div>
                    <div className="setup-blob blob-2"></div>
                    <div className="setup-blob blob-3"></div>
                </div>

                <div className="car-setup-card edit-mode">
                    <div className="setup-header">
                        <div className="setup-icon">
                            <Car size={32} />
                        </div>
                        <h1 className="setup-title">Edit Details</h1>
                    </div>

                    <div className="setup-form-grid">
                        {steps.filter(step => step.field !== 'mileage').map((stepItem) => (
                            <div key={stepItem.field} className="setup-form-item">
                                <label className="setup-label">{stepItem.label}</label>
                                <input
                                    type={stepItem.type}
                                    className="setup-input"
                                    placeholder={stepItem.placeholder}
                                    value={carData[stepItem.field]}
                                    onChange={(e) => setCarData({ ...carData, [stepItem.field]: stepItem.field === 'plateNumber' ? e.target.value.toUpperCase() : e.target.value })}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="setup-actions">
                        {onCancel && (
                            <button className="setup-btn-secondary" onClick={onCancel}>
                                {t.cancel || 'Cancel'}
                            </button>
                        )}
                        <button className="setup-btn" onClick={handleComplete}>
                            Save Changes <Check size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Wizard Mode
    return (
        <div className="car-setup-container">
            <div className="car-setup-background">
                <div className="setup-blob blob-1"></div>
                <div className="setup-blob blob-2"></div>
                <div className="setup-blob blob-3"></div>
            </div>

            <div className="car-setup-card">
                <div className="setup-header">
                    <div className="setup-icon">
                        <Car size={48} />
                    </div>
                    <h1 className="setup-title">{t.title}</h1>
                    <p className="setup-subtitle">
                        {t.step} {step} {t.of} {steps.length}
                    </p>
                </div>

                {/* Progress Bar */}
                <div className="progress-bar">
                    <div
                        className="progress-fill"
                        style={{ width: `${(step / steps.length) * 100}%` }}
                    ></div>
                </div>

                {/* Form Field */}
                <div className="setup-form">
                    <label className="setup-label">
                        {currentStep.label}
                        {currentStep.optional && <span style={{ fontWeight: 400, opacity: 0.5, marginLeft: '6px', fontSize: '0.85em' }}>(optional)</span>}
                    </label>
                    <input
                        type={currentStep.type}
                        className="setup-input"
                        placeholder={currentStep.placeholder}
                        value={carData[currentStep.field]}
                        onChange={(e) => setCarData({ ...carData, [currentStep.field]: currentStep.field === 'plateNumber' ? e.target.value.toUpperCase() : e.target.value })}
                        onKeyPress={handleKeyPress}
                        autoFocus
                    />
                </div>

                {/* Navigation */}
                <div className="setup-actions">
                    {onCancel && (
                        <button className="setup-btn-secondary" onClick={onCancel}>
                            {t.cancel || 'Cancel'}
                        </button>
                    )}
                    <button
                        className="setup-btn"
                        onClick={handleNext}
                        disabled={!currentStep.optional && !String(carData[currentStep.field]).trim()}
                    >
                        {step < steps.length ? (
                            <>
                                {t.next} <ChevronRight size={20} />
                            </>
                        ) : (
                            <>
                                {t.finish} <Check size={20} />
                            </>
                        )}
                    </button>
                </div>

                {/* Step Indicators */}
                <div className="step-indicators">
                    {steps.map((_, index) => (
                        <div
                            key={index}
                            className={`step-dot ${index + 1 <= step ? 'active' : ''}`}
                        ></div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CarSetupForm;
